import { css } from 'aphrodite';
import * as React from 'react';
import { IWillRenewPredictionReason } from '../../../extViewmodels';
import { NotepadTextIcon } from '../../../web/components/svgs/icons/NotepadTextIcon';
import { Button } from '../../../web/components-v2/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import { formatPredictionReasonTimestamp, getWillRenewPredictionReasonCategoryTitle } from './reasonCategories';
import { styleSheet } from './styles';

type Props = {
	reason?: IWillRenewPredictionReason;
	/** When provided, renders an "Edit reason" action inside the popover. */
	onEdit?: () => void;
};

/**
 * Notepad icon that, on click, shows the saved will-renew prediction reason (category, detail, and
 * the timestamp in Eastern Time). Renders nothing when no reason has been captured. Shared by the
 * monthly snapshot grid and the account detail page so the reason is discoverable in both places.
 */
export const PredictionReasonIndicator = ({ reason, onEdit }: Props) => {
	const hasReason = !!reason && (!!reason.category || !!reason.detail);
	if (!hasReason) {
		return null;
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant='ghost'
					size='icon'
					aria-label='View renewal prediction reason'
					title='View renewal prediction reason'
				>
					<NotepadTextIcon width={16} height={16} />
				</Button>
			</PopoverTrigger>
			<PopoverContent>
				<div className={css(styleSheet.reasonPopover)}>
					{!!reason?.category && (
						<div className={css(styleSheet.reasonPopoverRow)}>
							<span className={css(styleSheet.reasonPopoverLabel)}>Category</span>
							<span className={css(styleSheet.reasonPopoverCategory)}>
								{getWillRenewPredictionReasonCategoryTitle(reason.category)}
							</span>
						</div>
					)}
					{!!reason?.detail && (
						<div className={css(styleSheet.reasonPopoverRow)}>
							<span className={css(styleSheet.reasonPopoverLabel)}>Detail</span>
							<span className={css(styleSheet.reasonPopoverDetail)}>{reason.detail}</span>
						</div>
					)}
					{!!reason?.createdDate && (
						<div className={css(styleSheet.reasonPopoverRow)}>
							<span className={css(styleSheet.reasonPopoverLabel)}>Updated</span>
							<span className={css(styleSheet.reasonPopoverDetail)}>
								{formatPredictionReasonTimestamp(reason.createdDate)}
							</span>
						</div>
					)}
					{onEdit && (
						<Button variant='link' size='sm' onClick={onEdit} styles={[styleSheet.reasonPopoverEdit]}>
							Edit reason
						</Button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};
